/**
 * Selective Result Mapping for Smart Select Optimization.
 *
 * Converts rows returned from a selective projection query into the
 * SelectContext expected by the select callback, while guarding against
 * missing fields and unsupported "return whole node/edge" selections.
 */
import {
  normalizeRequiredRowTimestamp,
  normalizeRowTimestamp,
} from "../../backend/row-mappers";
import type { KindEntity } from "../../core/types";
import { compareStrings, hasOwnKey } from "../../utils";
import { createDataKeyedBag, isInteropProbeKey } from "../../utils/object";
import { mergeEdgeKinds, type SelectiveField, type Traversal } from "../ast";
import type {
  AliasMap,
  EdgeAliasMap,
  QueryBuilderState,
  SelectContext,
} from "../builder/types";
import {
  type FieldTypeInfo,
  type SchemaIntrospector,
} from "../schema-introspector";
import {
  containsSelectableAliasObject,
  SELECTABLE_ALIAS_MARKER,
  type SelectableAliasMarker,
} from "./selectable-alias";
import { decodeSelectedValue, nullToUndefined } from "./value-decoder";

// ============================================================
// Errors
// ============================================================

export class MissingSelectiveFieldError extends Error {
  readonly alias: string;
  readonly field: string;

  constructor(alias: string, field: string) {
    super(`Smart select missing field: ${alias}.${field}`);
    this.alias = alias;
    this.field = field;
  }
}

// ============================================================
// Internal Types
// ============================================================

type AliasKind = KindEntity;

type SystemFieldPlan = Readonly<{
  field: string;
  outputName: string;
}>;

type MetaFieldPlan = Readonly<{
  metaKey: string;
  outputName: string;
}>;

type PropsFieldPlan = Readonly<{
  field: string;
  outputName: string;
  typeInfo: FieldTypeInfo | undefined;
}>;

type AliasPlan = Readonly<{
  alias: string;
  kind: AliasKind;
  optional: boolean;
  systemFields: readonly SystemFieldPlan[];
  metaFields: readonly MetaFieldPlan[];
  propsFields: readonly PropsFieldPlan[];
  idOutputName: string | undefined;
  metaOutputNames: ReadonlySet<string>;
  propsOutputNames: ReadonlySet<string>;
  systemOutputNames: ReadonlySet<string>;
}>;

function normalizeMetaValue(
  value: unknown,
  metaKey: string,
  alias: string,
): unknown {
  if (metaKey === "version") return nullToUndefined(value);
  const field = `${alias}_${metaKey}`;
  if (metaKey === "createdAt" || metaKey === "updatedAt") {
    return normalizeRequiredRowTimestamp(value, field);
  }
  return normalizeRowTimestamp(value, field);
}

// ============================================================
// Marker for "whole alias object" detection
// ============================================================

// ============================================================
// Public API
// ============================================================

export function mapSelectiveResults<
  Aliases extends AliasMap,
  EdgeAliases extends EdgeAliasMap,
  R,
>(
  rows: readonly Record<string, unknown>[],
  state: QueryBuilderState,
  selectiveFields: readonly SelectiveField[],
  schemaIntrospector: SchemaIntrospector,
  selectFunction: (context: SelectContext<Aliases, EdgeAliases>) => R,
): readonly R[] {
  const plans = buildAliasPlans(state, selectiveFields, schemaIntrospector);

  return rows.map((row) => {
    const context = buildSelectiveContext<Aliases, EdgeAliases>(
      row,
      plans,
      state.traversals,
    );
    const result = selectFunction(context);

    // Returning whole alias objects is not supported by selective projection.
    // If it happens, fall back to the full fetch path.
    if (containsSelectableAliasObject(result)) {
      throw new MissingSelectiveFieldError(
        state.startAlias,
        "whole node/edge selection",
      );
    }

    return result;
  });
}

// ============================================================
// Plan Construction
// ============================================================

function buildAliasPlans(
  state: QueryBuilderState,
  selectiveFields: readonly SelectiveField[],
  schemaIntrospector: SchemaIntrospector,
): readonly AliasPlan[] {
  const aliasInfo = new Map<
    string,
    Readonly<{
      kind: AliasKind;
      optional: boolean;
      kindNames: readonly string[];
    }>
  >([
    [
      state.startAlias,
      {
        kind: "node",
        optional: false,
        kindNames: state.startKinds,
      },
    ],
  ]);

  for (const traversal of state.traversals) {
    const edgeKindNames = mergeEdgeKinds(traversal);

    aliasInfo.set(traversal.nodeAlias, {
      kind: "node",
      optional: traversal.optional,
      kindNames: traversal.nodeKinds,
    });
    aliasInfo.set(traversal.edgeAlias, {
      kind: "edge",
      optional: traversal.optional,
      kindNames: edgeKindNames,
    });
  }

  const fieldsByAlias = new Map<string, SelectiveField[]>();
  for (const field of selectiveFields) {
    const existing = fieldsByAlias.get(field.alias) ?? [];
    existing.push(field);
    fieldsByAlias.set(field.alias, existing);
  }

  const plans: AliasPlan[] = [];

  for (const [alias, info] of aliasInfo.entries()) {
    const fields = fieldsByAlias.get(alias) ?? [];

    const systemFields: SystemFieldPlan[] = [];
    const metaFields: MetaFieldPlan[] = [];
    const propsFields: PropsFieldPlan[] = [];

    for (const field of fields) {
      if (field.isSystemField) {
        if (field.field.startsWith("meta.")) {
          metaFields.push({
            metaKey: field.field.slice(5),
            outputName: field.outputName,
          });
        } else {
          systemFields.push({
            field: field.field,
            outputName: field.outputName,
          });
        }
      } else {
        const typeInfo =
          info.kind === "node" ?
            schemaIntrospector.getSharedFieldTypeInfo(
              info.kindNames,
              field.field,
            )
          : schemaIntrospector.getSharedEdgeFieldTypeInfo(
              info.kindNames,
              field.field,
            );

        propsFields.push({
          field: field.field,
          outputName: field.outputName,
          typeInfo,
        });
      }
    }

    const idOutputName =
      systemFields.find((f) => f.field === "id")?.outputName ?? undefined;

    plans.push({
      alias,
      kind: info.kind,
      optional: info.optional,
      systemFields,
      metaFields,
      propsFields,
      idOutputName,
      metaOutputNames: new Set(metaFields.map((f) => f.outputName)),
      propsOutputNames: new Set(propsFields.map((f) => f.outputName)),
      systemOutputNames: new Set(systemFields.map((f) => f.outputName)),
    });
  }

  // Keep plan iteration stable.
  return plans.toSorted((a, b) => compareStrings(a.alias, b.alias));
}

// ============================================================
// Context Building
// ============================================================

function buildSelectiveContext<
  Aliases extends AliasMap,
  EdgeAliases extends EdgeAliasMap,
>(
  row: Record<string, unknown>,
  plans: readonly AliasPlan[],
  traversals: readonly Traversal[],
): SelectContext<Aliases, EdgeAliases> {
  // Data-keyed: alias names chosen by the caller's query.
  const context = createDataKeyedBag<unknown>();

  for (const plan of plans) {
    const value =
      plan.optional && plan.idOutputName !== undefined ?
        buildOptionalAliasValue(row, plan)
      : buildRequiredAliasValue(row, plan);
    context[plan.alias] = value;
  }

  // Extract recursive depth/path values from the row
  for (const traversal of traversals) {
    const vl = traversal.variableLength;
    if (vl !== undefined) {
      if (vl.depthAlias !== undefined) {
        context[vl.depthAlias] =
          traversal.optional && row[vl.depthAlias] === null ?
            undefined
          : row[vl.depthAlias];
      }
      if (vl.pathAlias !== undefined) {
        context[vl.pathAlias] = row[vl.pathAlias];
      }
    }
  }

  // Spread at the boundary, for the same reason as the non-selective mapper:
  // this context reaches the caller's `select` callback.
  return { ...context } as SelectContext<Aliases, EdgeAliases>;
}

function buildOptionalAliasValue(
  row: Record<string, unknown>,
  plan: AliasPlan,
): unknown {
  if (plan.idOutputName === undefined) {
    return undefined;
  }
  const idValue = row[plan.idOutputName];

  if (idValue === null || idValue === undefined) {
    return undefined;
  }
  return buildRequiredAliasValue(row, plan);
}

function buildRequiredAliasValue(
  row: Record<string, unknown>,
  plan: AliasPlan,
): unknown {
  // Data-keyed: the projected field names are schema property names, and the
  // values behind them arrive as a `JSON.parse`d props bag — which yields
  // `__proto__` as an ordinary own key. The null prototype stays INTERNAL:
  // `createGuardedProxy` below spreads the finished bag onto an ordinary object
  // before proxying it, so nothing a caller can observe carries it.
  const base = createDataKeyedBag<unknown>();
  // The marker is keyed by SYMBOL, not by data; the cast only reaches the
  // symbol slot of a string-keyed record.
  (base as Record<symbol, unknown>)[SELECTABLE_ALIAS_MARKER] = {
    alias: plan.alias,
    kind: plan.kind,
  } satisfies SelectableAliasMarker;

  for (const field of plan.systemFields) {
    base[field.field] = nullToUndefined(row[field.outputName]);
  }

  if (plan.metaFields.length > 0) {
    const meta = createDataKeyedBag<unknown>();
    for (const field of plan.metaFields) {
      meta[field.metaKey] = normalizeMetaValue(
        row[field.outputName],
        field.metaKey,
        plan.alias,
      );
    }
    base["meta"] = createGuardedProxy(meta, `${plan.alias}.meta`);
  }

  for (const field of plan.propsFields) {
    const decoded = decodeSelectedValue(row[field.outputName], field.typeInfo);
    base[field.field] = decoded;
  }

  return createGuardedProxy(base, plan.alias);
}

/**
 * Wraps a projected alias (or its `meta`) in the missing-field guard, ON AN
 * ORDINARY OBJECT.
 *
 * The spread is the boundary spread `createDataKeyedBag` documents, applied
 * HERE rather than at each call site because a proxy's target is itself
 * caller-observable: the `get` trap below re-supplies `Object.prototype`'s
 * members explicitly, but `instanceof`, `Object.getPrototypeOf`, and every
 * other internal method fall through to the TARGET, which no `get` trap can
 * disguise. Proxying a null-prototype bag therefore made `ctx.p instanceof
 * Object` answer `false` under smart selection while the full mapper — which
 * builds ordinary object literals (see `buildSelectableNode`) — answered
 * `true`: a public behavior that depended on whether the optimizer engaged.
 *
 * Spreading is safe precisely where a key-by-key rebuild would not be: it
 * copies own properties (including the alias marker symbol) with
 * CreateDataProperty rather than Set, so a projected field named `__proto__`
 * survives as an own key while `Object.prototype` is restored.
 *
 * Callers must therefore hand over a COMPLETE bag — the copy is taken here and
 * later writes to the original are invisible.
 */
function createGuardedProxy(
  target: Record<string, unknown>,
  debugPath: string,
): unknown {
  return new Proxy(
    { ...target },
    {
      get: (object, property: string | symbol, receiver) => {
        if (typeof property === "symbol") {
          return Reflect.get(object, property, receiver) as unknown;
        }

        // Own keys FIRST — before any protocol-name exemption. The projected
        // row's keys are data, so `in` here would answer for `Object.prototype`
        // members the row does not carry, and a name-identity short-circuit
        // ahead of this branch would answer for a field the row DOES carry.
        // Smart selection must be indistinguishable from the full mapper, whose
        // plain objects hand back an own `then` / `toJSON` like any other
        // property; a schema may declare either name, and such a field survives
        // validation and the JSON round-trip as ordinary data. The boundary
        // spread above preserves that: it copies own properties with
        // CreateDataProperty, so every projected field — `__proto__` included —
        // is still an own key of the target this branch reads.
        if (hasOwnKey(object, property)) {
          return Reflect.get(object, property, receiver);
        }

        // Reached only once the row is known NOT to carry the key: resolve the
        // language's own probes to `undefined` so a partially projected row is
        // not mistaken for a thenable by `await` and `JSON.stringify` still
        // works on it. Returning stored data above is safe for `then` because
        // props decode from JSON and can never be callable — the thenable check
        // ignores a non-callable `then`, exactly as it does on the full path.
        if (isInteropProbeKey(property)) {
          return;
        }

        // Deliberately `in`, NOT hasOwnKey: this branch's whole purpose is to
        // reach inherited `Object.prototype` members (`toString`, `valueOf`, …) so
        // a projected row still behaves like an object. Leave it alone.
        if (property in Object.prototype) {
          return Reflect.get(Object.prototype, property, receiver) as unknown;
        }

        throw new MissingSelectiveFieldError(debugPath, property);
      },
    },
  );
}
